/**
 * FM-S-multi-4-1 — Active entity Zustand store.
 *
 * Хранит:
 * - activeEntityId — текущий entity context
 * - activeScope — 'entity' (single) | { type: 'holding', holdingId } (aggregate) | 'all' (super-admin)
 * - accessibleEntityIds — список UUID, к которым у user есть доступ (или ['*'] для super)
 * - memberships — список MembershipResolved для UI (group by holding и т.п.)
 * - isSuperAdmin
 *
 * Persistence:
 * - activeEntityId сохраняется в localStorage key `staiter.active_entity_id`
 * - stale ID (∉ accessible_entity_ids) → reset + showStaleWarning
 *
 * Hydration chain: localStorage → me.active_entity_id → me.default_entity_id → accessible[0]
 */
import { create } from 'zustand';

/** B42: `module_access` — модули, открытые пользователю в ЭТОЙ компании (экран отказа, F108). */
type MembershipResolved =
  | {
      source: 'direct';
      entity_id: string;
      entity_name: string;
      role: string;
      module_access?: string[];
    }
  | {
      source: 'holding';
      entity_id: string;
      entity_name: string;
      holding_id: string;
      holding_name: string;
      role: string;
      module_access?: string[];
    };

export type ActiveScope = 'entity' | { type: 'holding'; holdingId: string } | 'all';

type MeResponseLike = {
  is_super_admin?: boolean;
  default_entity_id?: string | null;
  active_entity_id?: string | null;
  accessible_entity_ids?: string[];
  memberships?: MembershipResolved[];
};

type ActiveEntityState = {
  activeEntityId: string | null;
  activeScope: ActiveScope;
  accessibleEntityIds: string[];
  memberships: MembershipResolved[];
  isSuperAdmin: boolean;
  isInitialized: boolean;
  staleStoredId: string | null;
  setActiveEntity: (entityId: string) => void;
  setActiveScope: (scope: ActiveScope) => void;
  hydrateFromMe: (me: MeResponseLike) => void;
  clearStaleWarning: () => void;
  /**
   * С-I2 Р.3 — доступ к выбранной компании отозван посреди сессии (403 на `/me`): выбор снят,
   * стор выбирает заново по свежему профилю и показывает предупреждение об устаревшей компании.
   */
  revokeActiveEntity: (me: MeResponseLike) => void;
  clear: () => void;
};

const STORAGE_KEY = 'staiter.active_entity_id';
// tasks-by-company F1 — персист scope, чтобы агрегат (холдинг/все компании) переживал
// перезагрузку (раньше hydrateFromMe всегда сбрасывал в 'entity').
const SCOPE_KEY = 'staiter.active_scope';

function readStoredId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * С-I3 Р.1, круг 1 гейта (UX, MAJOR) — имена компаний этого браузера. После отзыва доступа и
 * перезагрузки компания исчезает из профиля, а предупреждение должно назвать именно её.
 */
const NAMES_KEY = 'staiter.entity_names';

export function readEntityName(id: string): string | null {
  try {
    const map = JSON.parse(localStorage.getItem(NAMES_KEY) ?? '{}') as Record<string, unknown>;
    const v = map[id];
    return typeof v === 'string' && v ? v : null;
  } catch {
    return null;
  }
}

function rememberEntityNames(memberships: readonly MembershipResolved[]): void {
  try {
    const map = JSON.parse(localStorage.getItem(NAMES_KEY) ?? '{}') as Record<string, string>;
    for (const ms of memberships) map[ms.entity_id] = ms.entity_name;
    localStorage.setItem(NAMES_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

/** Сериализация scope в строку: 'entity' | 'all' | 'holding:<id>'. */
function writeStoredScope(scope: ActiveScope): void {
  try {
    const s =
      scope === 'all' ? 'all' : typeof scope === 'object' ? `holding:${scope.holdingId}` : 'entity';
    localStorage.setItem(SCOPE_KEY, s);
  } catch {
    // ignore
  }
}

/**
 * access-go-live hotfix 2026-07-09 — явный сигнал смены компании/охвата.
 * На проде zustand-подписка из active-entity-sync не срабатывала на клик
 * в EntitySwitcher (store обновлялся: chip/localStorage/headers — а листенер
 * молчал; на boot-hydrate срабатывал). Событие диспатчится ИЗ САМОГО store —
 * той же модульной инстанции, которую переключатель гарантированно мутирует.
 */
export const SCOPE_CHANGED_EVENT = 'staiter:active-scope-changed';

function emitScopeChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(SCOPE_CHANGED_EVENT));
  } catch {
    // SSR/test — ignore
  }
}

function readStoredScope(): ActiveScope | null {
  try {
    const s = localStorage.getItem(SCOPE_KEY);
    if (s === 'all') return 'all';
    if (s?.startsWith('holding:')) {
      const holdingId = s.slice('holding:'.length);
      return holdingId ? { type: 'holding', holdingId } : null;
    }
    if (s === 'entity') return 'entity';
    return null;
  } catch {
    return null;
  }
}

export const useActiveEntityStore = create<ActiveEntityState>((set) => ({
  activeEntityId: null,
  activeScope: 'entity',
  accessibleEntityIds: [],
  memberships: [],
  isSuperAdmin: false,
  isInitialized: false,
  staleStoredId: null,

  setActiveEntity: (entityId) => {
    writeStoredId(entityId);
    writeStoredScope('entity');
    set({ activeEntityId: entityId, activeScope: 'entity' });
    emitScopeChanged();
  },

  setActiveScope: (scope) => {
    writeStoredScope(scope);
    if (scope === 'entity') {
      // setEntity отдельно
      set({ activeScope: 'entity' });
    } else {
      set({ activeScope: scope });
    }
    emitScopeChanged();
  },

  hydrateFromMe: (me) => {
    const isSuper = me.is_super_admin ?? false;
    const accessible = me.accessible_entity_ids ?? [];
    const memberships = me.memberships ?? [];
    rememberEntityNames(memberships);

    const stored = readStoredId();
    let resolved: string | null = null;
    let staleStoredId: string | null = null;

    if (isSuper) {
      // super-admin может switch куда угодно; honor stored if accessible_entity_ids includes
      // (или sentinel '*' = ALL).
      if (stored) resolved = stored;
      else if (me.default_entity_id) resolved = me.default_entity_id;
      else if (me.active_entity_id) resolved = me.active_entity_id;
    } else {
      if (stored && accessible.includes(stored)) {
        resolved = stored;
      } else if (stored) {
        // stored ID есть, но в accessible его нет — stale → warn + reset
        staleStoredId = stored;
        writeStoredId(null);
        resolved =
          me.default_entity_id && accessible.includes(me.default_entity_id)
            ? me.default_entity_id
            : me.active_entity_id && accessible.includes(me.active_entity_id)
              ? me.active_entity_id
              : (accessible[0] ?? null);
      } else {
        // С-I3 Р.1 — «активная» из профиля берётся, только если доступна: отключённая компания
        // по умолчанию иначе выбиралась снова, и каждый запрос получал 403.
        const pick = [me.active_entity_id, me.default_entity_id].find(
          (id): id is string => !!id && (accessible.length === 0 || accessible.includes(id)),
        );
        resolved = pick ?? accessible[0] ?? me.active_entity_id ?? null;
      }
    }

    if (resolved) writeStoredId(resolved);

    // tasks-by-company F1 — восстановить сохранённый агрегат-режим (холдинг/все компании),
    // чтобы он переживал перезагрузку. Дефолт — 'entity' (одна активная компания).
    const restoredScope = readStoredScope() ?? 'entity';

    set({
      activeEntityId: resolved,
      activeScope: restoredScope,
      accessibleEntityIds: accessible,
      memberships,
      isSuperAdmin: isSuper,
      isInitialized: true,
      staleStoredId,
    });
  },

  clearStaleWarning: () => set({ staleStoredId: null }),

  revokeActiveEntity: (me) => {
    const revoked = useActiveEntityStore.getState().activeEntityId;
    writeStoredId(null);
    useActiveEntityStore.getState().hydrateFromMe(me);
    set({ staleStoredId: revoked });
    emitScopeChanged();
  },

  clear: () => {
    writeStoredId(null);
    try {
      localStorage.removeItem(SCOPE_KEY);
    } catch {
      // ignore
    }
    set({
      activeEntityId: null,
      activeScope: 'entity',
      accessibleEntityIds: [],
      memberships: [],
      isSuperAdmin: false,
      isInitialized: false,
      staleStoredId: null,
    });
  },
}));

/** Helper для не-React контекстов (interceptor). */
export function getActiveEntityHeaders(): Record<string, string> {
  const s = useActiveEntityStore.getState();
  // all-scope (S9) — init-race фикс: route-loader'ы на холодной загрузке (refresh / прямой
  // URL / новая вкладка) стартуют ДО `hydrateFromMe` (он ждёт /auth/me) → раньше слался
  // пустой {} → BE уходил в entity-scope → 404 на кросс-компанийном ресурсе в «Все компании».
  // Scope/entity уже персистятся в localStorage синхронно — читаем их до инициализации,
  // чтобы холодный запрос нёс корректный scope-заголовок.
  if (!s.isInitialized) {
    const scope = readStoredScope();
    if (scope === 'all') return { 'X-Active-Scope': 'all' };
    if (typeof scope === 'object' && scope?.type === 'holding') {
      return { 'X-Active-Scope': `holding:${scope.holdingId}` };
    }
    const storedId = readStoredId();
    return storedId ? { 'X-Active-Entity': storedId } : {};
  }
  // tasks-by-company F1 — «Все компании»: явный scope-сигнал (раньше слался пустой {},
  // и BE уходил на одну компанию). BE (withActiveEntity) понимает `X-Active-Scope: all`.
  if (s.activeScope === 'all') return { 'X-Active-Scope': 'all' };
  if (typeof s.activeScope === 'object' && s.activeScope.type === 'holding') {
    return { 'X-Active-Scope': `holding:${s.activeScope.holdingId}` };
  }
  if (s.activeEntityId) {
    return { 'X-Active-Entity': s.activeEntityId };
  }
  return {};
}
